import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import metaIcon from '@/assets/icons/meta-icon.svg';
import linkedinIcon from '@/assets/icons/linkedin-icon.svg';
import googleIcon from '@/assets/icons/post-google.svg';
import IntegrationConnectBtn from './connect-button';
import IntegrationDisconnect from './disconnect-button';
import PlatformPageLinking, { PlatformConfig } from './platform-page-linking';
import { cleanTypeName } from '@/lib/utils';
import {
  PostChannel,
  SchedulingVariantV1Channel,
} from '@/__generated__/graphql';
import { useOrganizationContext } from '@/contexts/organization-context';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import Image from 'next/image';
import { gql } from '@/__generated__';
import SettingsTabTitle from '@/components/compound-components/settings/settings-tab-title';
import { useSettingsConntext } from '@/contexts/settings-context';
import { useRouter } from 'next/router';
import { useGoogleConnectionStatus } from '@/lib/auth-service';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const SchedulingVariantGetByOrganization = gql(`
  query FetchVariantByOrgIdIntegrations(
    $input: SchedulingVariantV1GetByOrganizationInput!
  ) {
    schedulingVariantV1 {
      getByOrganization(input: $input) {
        ... on SchedulingVariantV1GetByOrganizationSuccess {
          items {
            item {
              id
              channels {
                channel
                pageId
                weight
                name
              }
            }
          }
        }
      }
    }
  }
`);

export type Variant = {
  variantId: string;
  existingChannels: SchedulingVariantV1Channel[];
};

interface IntegrationsTabProps {
  currentTab: 'Meta' | 'LinkedIn' | 'Google';
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ currentTab }) => {
  const { t } = useTranslation();
  const isGsuiteIntegrationEnabled =
    !!useFeatureFlagEnabled('gsuite-integration');
  const enableFBLoginForBusiness = useFeatureFlagEnabled(
    'fb-login-for-business'
  );
  const { organizationId } = useOrganizationContext();
  const { integrationShowPageLinking } = useSettingsConntext();
  const session = useSessionContext();
  const [variant, setVariant] = useState<Variant>();
  const [linkedSocialMedia, setLinkedSocialMedia] = useState<
    SchedulingVariantV1Channel[]
  >([]);
  const [connectedGoogleAccount, setConnectedGoogleAccount] = useState('');
  const router = useRouter();
  const { isHandleCallback } = router.query;
  const { setIntegrationLoading } = useSettingsConntext();
  const { isGoogleConnected, connectedGoogleEmail } = useGoogleConnectionStatus(
    isGsuiteIntegrationEnabled
  );

  useEffect(() => {
    if (isHandleCallback) {
      setIntegrationLoading && setIntegrationLoading(true);
    }
  }, [isHandleCallback, setIntegrationLoading]);

  useEffect(() => {
    if (isGoogleConnected) {
      setConnectedGoogleAccount(connectedGoogleEmail);
    }
  }, [isGoogleConnected, connectedGoogleEmail]);

  const getIntegrationConfig = (enableFBLoginForBusiness = true) => {
    const commonGoogleConfig: PlatformConfig = {
      icon: googleIcon,
      thirdPartyId: 'google',
      platform: 'google',
      channels: [PostChannel.Unspecified],
    };

    const commonLinkedInConfig: PlatformConfig = {
      icon: linkedinIcon,
      thirdPartyId: 'custom-linkedin',
      platform: 'linkedin',
      channels: [PostChannel.LinkedIn],
    };

    const newLoginConfig: {
      Meta: PlatformConfig;
      LinkedIn: PlatformConfig;
      Google: PlatformConfig;
    } = {
      Meta: {
        icon: metaIcon,
        thirdPartyId: 'facebook-business',
        platform: 'meta',
        channels: [PostChannel.Instagram, PostChannel.Facebook],
      },
      LinkedIn: commonLinkedInConfig,
      Google: commonGoogleConfig,
    };

    const oldLoginConfig: {
      Meta: PlatformConfig;
      LinkedIn: PlatformConfig;
      Google: PlatformConfig;
    } = {
      Meta: {
        icon: metaIcon,
        thirdPartyId: 'facebook',
        platform: 'meta',
        channels: [PostChannel.Instagram, PostChannel.Facebook],
      },
      LinkedIn: commonLinkedInConfig,
      Google: commonGoogleConfig,
    };

    return enableFBLoginForBusiness ? newLoginConfig : oldLoginConfig;
  };

  const [integrationConfig, setIntegrationConfig] = useState<{
    Meta: PlatformConfig;
    LinkedIn: PlatformConfig;
    Google: PlatformConfig;
  }>(getIntegrationConfig());

  const { data: variantData } = useQuery(SchedulingVariantGetByOrganization, {
    skip:
      !organizationId ||
      !(!session.loading && session.accessTokenPayload['st-role']?.v),
    variables: { input: { organizationId: organizationId || '' } },
  });

  const getVariant = useCallback(async () => {
    //  console.log('==============getVariant================');
    if (
      !(!session.loading && session.accessTokenPayload['st-role']?.v) ||
      !organizationId
    ) {
      return { variantId: '', existingChannels: [] };
    }

    if (!variantData?.schedulingVariantV1.getByOrganization.items[0]) {
      return { variantId: '', existingChannels: [] };
    }

    const { id: variantId, channels: existingChannels } = cleanTypeName(
      variantData?.schedulingVariantV1.getByOrganization.items[0]?.item
    );

    return { variantId, existingChannels } as Variant;
  }, [organizationId, session, variantData]);

  useEffect(() => {
    getVariant().then((data) => {
      setVariant(data);
      setLinkedSocialMedia(data.existingChannels);
    });
  }, [getVariant]);

  useEffect(() => {
    setIntegrationConfig(getIntegrationConfig(enableFBLoginForBusiness));
  }, [enableFBLoginForBusiness]);

  const shouldShowGoogleTab =
    integrationConfig[currentTab].platform === 'google';

  const isShowConnect = !linkedSocialMedia.some((vc) => {
    if (shouldShowGoogleTab && connectedGoogleAccount) {
      return true;
    }

    return (
      vc.pageId &&
      vc.channel ===
        integrationConfig[currentTab].channels.find(
          (c: PostChannel) => c === vc.channel
        )
    );
  });

  if (session.loading || !session.doesSessionExist) {
    return null;
  }

  return (
    <div>
      <SettingsTabTitle title={`${currentTab} Workspace`} />
      {organizationId &&
      ((variant && variant.variantId) || shouldShowGoogleTab) ? (
        isShowConnect ? (
          <div className='flex items-center justify-between rounded border border-solid border-gray-100 px-3 py-6'>
            <div className='flex items-center gap-2'>
              <Image
                src={integrationConfig[currentTab].icon?.src}
                width={integrationConfig[currentTab].icon?.width}
                height={integrationConfig[currentTab].icon?.height}
                alt={integrationConfig[currentTab].thirdPartyId}
              />
              <p>{t(`connect-text.${currentTab}`)}</p>
            </div>
            <IntegrationConnectBtn
              thirdPartyId={integrationConfig[currentTab].thirdPartyId}
              socialMediaPlatform={integrationConfig[currentTab].platform}
              variant={variant || { variantId: '', existingChannels: [] }}
              setLinkedSocialMedia={setLinkedSocialMedia}
              setConnectedGoogleAccount={setConnectedGoogleAccount}
            />
          </div>
        ) : (
          <IntegrationDisconnect
            linkedChannels={linkedSocialMedia}
            platformChannels={integrationConfig[currentTab].channels}
            setLinkedSocialMedia={setLinkedSocialMedia}
            variant={variant || { variantId: '', existingChannels: [] }}
            platform={integrationConfig[currentTab].platform}
            googleAccount={connectedGoogleAccount}
            setConnectedGoogleAccount={setConnectedGoogleAccount}
          />
        )
      ) : (
        <>
          {!organizationId && (
            <p>
              {t('social-media-access-management.select-organization-first')}.{' '}
            </p>
          )}
          {!variant ||
            (!variant.variantId && (
              <p>
                {t('social-media-access-management.org-must-have-variant')}.{' '}
              </p>
            ))}
        </>
      )}

      {integrationShowPageLinking && variant && (
        <PlatformPageLinking
          currentTab={currentTab}
          variant={variant}
          setLinkedSocialMedia={setLinkedSocialMedia}
          integrationConfig={integrationConfig}
        />
      )}
    </div>
  );
};

export default IntegrationsTab;
